import { computed, type ComputedRef, ref, watch } from 'vue'

import { CartMutationKind, type CartMutationRecord, useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'
import { useMainStore } from '@/stores/main'

import { usePreferredReducedMotion } from '@vueuse/core'

import { useEnhancedFlyout } from '@/composables/useEnhancedFlyout'
import { type FreeGiftCalloutProps, useFreeGift } from '@/composables/useFreeGift'
import {
  RewardSequencePhase,
  RewardSettleReason,
  useRewardSequence
} from '@/composables/useRewardSequence'
import { useRewardThresholdCrossing } from '@/composables/useRewardThresholdCrossing'
import { rewardRailFillPercent } from '@/lib/rewardLadder'
import { FILL_DURATION_MS, FILL_EASING } from '@/lib/rewardSequenceTimings'

import type { RewardCelebrationStage } from '@/components/Shared/RewardsTracker/RewardCelebrationStage'

import type { RewardTier } from '@/types/RewardTier'

export interface UseCartFlyoutSequence {
  /** Rail fill the sequence owns, or `null` to hand the bar back to its resting derivation. */
  trackerFillPercent: ComputedRef<number | null>
  /** Inline transition applied to the rail while the sequence owns it. */
  fillTransition: ComputedRef<string | null>
  /** The one tier celebrating, by promotion id. */
  celebratingTierId: ComputedRef<string | null>
  /** How far its node has got through the celebration. */
  celebrationStage: ComputedRef<RewardCelebrationStage>
  /** Tiers whose nodes must still render pre-crossing, though their data already says unlocked. */
  heldTierIds: ComputedRef<readonly string[]>
  /** Free Gift banner props, carrying **both** rewards while a crossing is celebrating. */
  freeGiftCallout: ComputedRef<FreeGiftCalloutProps | null>
  /** Whether the banner shows its unlocked half. */
  isFreeGiftUnlocked: ComputedRef<boolean>
  /** Promotion ids whose reward rows are withheld until the reveal beat. */
  withheldRewardTierIds: ComputedRef<readonly string[]>
  /** Promotion ids whose reward rows the reveal beat has just brought in — plays the arrival flash. */
  arrivingRewardTierIds: ComputedRef<readonly string[]>
  /** The unlock announcement for the panel's live region. Empty at rest. */
  announcement: ComputedRef<string>
  /** Collapse the run to its end state — wired to the panel's `interact`. */
  settleOnInteraction: () => void
  /**
   * Whether the open now being observed was the sequence's, clearing the answer as it gives it.
   *
   * It exists for one reason: `view_cart` fires from a watcher on `showCartSidebar`, which can see
   * *that* the panel opened but not *why* — and auto-opening on every add would otherwise roughly
   * double the event. So an automatic open fires `view_cart_auto` instead, same payload, and the pair
   * reads together in reporting (Gap 18).
   */
  consumeAutoOpen: () => boolean
}

/**
 * Drives the reward-unlock sequence from **real cart mutations** — SUN-292 Gap 15, iteration A2b.
 *
 * The debug harness (Gap 13) has driven the same machine from fixtures since phase 1. This is the
 * second caller the input contract was written for: it computes `{ unlockedTiers, fromPct, toPct }`
 * from the live cart and hands it over. The machine still subscribes to nothing.
 *
 * ---
 *
 * **It takes two inputs, and needing two is the part that is easy to miss.**
 *
 * `useRewardThresholdCrossing` cannot be the trigger on its own: it fires `onAdded` only when something
 * newly unlocked, and that is the *minority* of adds. The business requires every add to open the cart,
 * fill the bar, animate the row in and close again — so hanging the sequence off the crossing alone
 * leaves the common path silent, which is exactly the state phase 1 was trying to end. Removes have the
 * same shape: a remove that re-locks nothing still has to pull the bar backwards, which is what
 * `RewardFillDirection.Emptying` exists for.
 *
 * So:
 *
 * - **`cartStore.lastCartMutation`** says *a run should start, and here is the subtotal it started
 *   from*. Published by the mutating actions themselves rather than inferred from a subtotal watcher,
 *   because a persisted cart the server re-prices on load changes the subtotal without anyone adding
 *   anything — and that would fly the flyout open at page load once auto-open lands.
 * - **`useRewardThresholdCrossing`** answers *did anything unlock, and what*. It stays a detector; on
 *   the common path it simply has not fired and `unlockedTiers` is empty, which is the case `start()`
 *   already handles by taking its early return.
 *
 * The crossing arrives on a pre-flush watcher and this consumes it on a **post-flush** one, so the
 * ordering is a guarantee of the scheduler rather than of declaration order. It is consumed once and
 * matched against the mutation's own pre-mutation subtotal, so a crossing detected outside a mutation
 * can never be picked up by the next one.
 *
 * ---
 *
 * **It also owns the drawer's two automatic movements** (A3, Gaps 14 / 18 / 20), and both are caller
 * behaviour by design — the machine reaches a state and never touches a store, which is what keeps it
 * drivable from the sandbox. Opening is {@link shouldAutoOpen}; closing is the watcher on `Closing`,
 * which *is* the 6,000ms beat rather than a reaction to it.
 *
 * A run only starts when it has somewhere to play: with the cart closed and nothing about to open it,
 * there is nothing to animate and no run.
 */
export function useCartFlyoutSequence(): UseCartFlyoutSequence {
  const cartStore = useCartStore()
  const catalogStore = useCatalogStore()
  const mainStore = useMainStore()

  const { isEnhancedFlyoutActive } = useEnhancedFlyout()
  const sequence = useRewardSequence()
  const prefersReducedMotion = usePreferredReducedMotion()

  /** The subtotal the current run started from, so the banner can be shown the cart as it was. */
  const runFromSubtotal = ref(0)

  /**
   * The crossing detected in this flush, waiting for the mutation that caused it.
   *
   * Held rather than acted on directly because a crossing is only half the story — the run needs the
   * mutation's pre-mutation subtotal for `fromPct`, and the crossing is not the only thing that starts
   * a run.
   */
  const pendingCrossing = ref<{ previousSubtotal: number; unlockedTiers: RewardTier[] } | null>(
    null
  )

  // A second instance of the detector, alongside App.vue's. Deliberate: the composable is detection
  // only and holds its own baseline, so two consumers asking the same question independently is
  // cheaper and clearer than routing one instance's callbacks through an app-level bus. `onRemoved` is
  // unused here — a re-lock reaches this composable as an ordinary mutation with nothing unlocked,
  // which is the run that empties the bar.
  useRewardThresholdCrossing({
    onAdded: (crossing) => {
      pendingCrossing.value = {
        previousSubtotal: crossing.previousSubtotal,
        unlockedTiers: crossing.unlockedTiers
      }
    },
    onRemoved: () => {}
  })

  /**
   * Whether the drawer opens itself for this mutation (Gap 14).
   *
   * **Adds only.** The business asked for the cart to open "on every add"; nothing asks for it to open
   * on a remove, and a cart that flew open because the customer *deselected* a package on the catalog
   * step would be intrusive. A remove still runs the sequence when the cart is already open — the rail
   * travels backwards, which is what `RewardFillDirection.Emptying` is for.
   *
   * **Minus the per-mutation suppression** (Gap 20). See {@link CartMutationRecord.suppressCartFlyout}:
   * it is the causal axis the positional gate deliberately refuses to answer.
   */
  function shouldAutoOpen(record: CartMutationRecord): boolean {
    return record.kind === CartMutationKind.Add && !record.suppressCartFlyout
  }

  /**
   * Whether the open now in progress was the sequence's doing rather than the customer's.
   *
   * A plain variable, not a ref: it is read once by the `showCartSidebar` watcher in the same flush it
   * is set, and nothing renders from it. Reactivity would only invite something to.
   */
  let openedAutomatically = false

  /**
   * Takes the crossing belonging to this mutation, if there is one.
   *
   * Consume-once, plus a match on the subtotal both sides agree the cart was at. Together they mean a
   * crossing the detector reported for something other than a customer mutation — a cart load that
   * re-priced, say — cannot be celebrated by the next add that happens along. Exact equality is safe:
   * both values are the same `subtotalPostDiscounts` field, not the result of arithmetic.
   */
  function takeCrossingFor(previousSubtotal: number): RewardTier[] {
    const pending = pendingCrossing.value
    pendingCrossing.value = null

    return pending && pending.previousSubtotal === previousSubtotal ? pending.unlockedTiers : []
  }

  watch(
    () => cartStore.lastCartMutation,
    (record) => {
      if (!record) return

      // Read at fire time rather than at wiring time, so the gate follows the customer's route rather
      // than whichever route was current when the flyout mounted. Clearing the pending crossing on an
      // excluded route matters: it stops one detected there being carried onto an enhanced one.
      if (!isEnhancedFlyoutActive.value) {
        pendingCrossing.value = null
        return
      }

      // Read before anything opens the drawer. It governs both the timings and the ending: a cart the
      // customer opened is not the sequence's to put away.
      const warmOpen = mainStore.showCartSidebar
      const willAutoOpen = shouldAutoOpen(record)

      // **A run needs somewhere to play.** With the cart closed and nothing about to open it — a
      // remove, or an upsell add on its way to the next page — the beats would run against a drawer
      // nobody can see, and a customer who opened the cart part-way through would walk into a
      // celebration already in progress. Nothing to animate, so nothing runs.
      if (!warmOpen && !willAutoOpen) {
        pendingCrossing.value = null
        return
      }

      const previousSubtotal = record.previousSubtotal
      const unlockedTiers = takeCrossingFor(previousSubtotal)

      // Opened before `start()` so the drawer's slide and the machine's clock begin together — the
      // 500ms drawer beat is the sequence's first, and the panel's transform has something to
      // interpolate from because the panel is always mounted.
      //
      // Guarded on the cart being **closed**, not merely on the mutation being auto-openable. An add
      // to an already-open cart opens nothing, so `showCartSidebar` does not change, so the watcher
      // that consumes the flag never fires — and the flag would sit there waiting to mislabel the next
      // open the customer performed themselves as automatic.
      if (willAutoOpen && !warmOpen) {
        openedAutomatically = true
        mainStore.showCartSidebar = true
      }

      runFromSubtotal.value = previousSubtotal
      sequence.start(
        {
          unlockedTiers,
          fromPct: rewardRailFillPercent(catalogStore.rewardTiers(previousSubtotal)),
          toPct: rewardRailFillPercent(catalogStore.rewardTiers(cartStore.effectiveSubtotal))
        },
        {
          warmOpen,
          reducedMotion: prefersReducedMotion.value === 'reduce'
        }
      )
    },
    // After the crossing detector's pre-flush watcher has run, so the crossing for this mutation is
    // already recorded whichever order the two were declared in.
    { flush: 'post' }
  )

  // The machine reaches `Closing`; it never touches the drawer, which is what keeps it store-free. So
  // this watcher *is* the 6,000ms auto-close beat rather than a convenience — the beat silently does
  // nothing until a caller opts in.
  //
  // **`flush: 'sync'` is load-bearing.** `Closing` and the `Idle` after it are consecutive values of
  // one ref, 500ms apart on the clock but not necessarily in separate ticks: throttle the timer — a
  // backgrounded tab drops it to roughly 1Hz — and both come due in one pass. A batched watcher then
  // sees only the last value, never observes `Closing`, and the drawer stays open for good.
  watch(
    () => sequence.phase.value,
    (next) => {
      if (next === RewardSequencePhase.Closing) mainStore.showCartSidebar = false
    },
    { flush: 'sync' }
  )

  // Closing the flyout by hand abandons the run rather than settling it — settling would leave a
  // celebration playing out behind a panel the customer has just dismissed. Guarded on `Closing`,
  // because the beat above closes the drawer itself and must not be read as the customer doing it.
  watch(
    () => mainStore.showCartSidebar,
    (open) => {
      if (open) return
      if (sequence.phase.value === RewardSequencePhase.Closing) return
      if (sequence.isRunning.value) sequence.cancel()
    }
  )

  /**
   * The banner holds its pre-crossing subject until the card-advance beat, exactly as the tracker's
   * nodes and the reward rows do. `cardAdvanced` is the other half of that beat.
   */
  const heldSubtotal = computed<number | null>(() =>
    sequence.isRunning.value && !sequence.cardAdvanced.value ? runFromSubtotal.value : null
  )

  // The machine's refs are `readonly()` for its own consumers' sake; the tier itself is an ordinary
  // `RewardTier`, so this hands it on as one rather than growing a second "which tier celebrates" rule.
  const celebratedTier = computed<RewardTier | null>(
    () => (sequence.celebratedTier.value ?? null) as RewardTier | null
  )

  const { callout: freeGiftCallout } = useFreeGift({ heldSubtotal, celebratedTier })

  return {
    trackerFillPercent: computed(() => (sequence.isRunning.value ? sequence.fillPct.value : null)),
    fillTransition: computed(() =>
      sequence.isRunning.value ? `width ${FILL_DURATION_MS}ms ${FILL_EASING}` : null
    ),
    celebratingTierId: computed(() => sequence.celebratedTier.value?.promotionId ?? null),
    celebrationStage: computed(() => sequence.celebrationStage.value),
    heldTierIds: computed(() => sequence.heldTierIds.value),
    freeGiftCallout,
    // The unlocked half runs from the celebration beat until the card advances — a two-second window,
    // not "until the run ends". Past the advance the banner is showing the next locked reward, and an
    // unlocked frame over it would be describing something else.
    isFreeGiftUnlocked: computed(
      () =>
        !sequence.cardAdvanced.value &&
        (sequence.phase.value === RewardSequencePhase.Celebrating ||
          sequence.phase.value === RewardSequencePhase.Settling)
    ),
    // **Every** row this crossing granted, not just the celebrated one: a multi-tier add grants several
    // from one response, and letting the uncelebrated ones drop in at t=0 while the last waits four
    // seconds would look arbitrary.
    withheldRewardTierIds: computed(() =>
      sequence.rewardRowVisible.value ? [] : sequence.runUnlockedTierIds.value
    ),
    arrivingRewardTierIds: computed(() =>
      sequence.isRunning.value && sequence.rewardRowVisible.value
        ? sequence.runUnlockedTierIds.value
        : []
    ),
    announcement: computed(() => sequence.announcement.value),
    settleOnInteraction: () => sequence.settle(RewardSettleReason.Interaction),
    consumeAutoOpen: () => {
      const wasAutomatic = openedAutomatically
      openedAutomatically = false
      return wasAutomatic
    }
  }
}
